import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  login,
  clearError,
  setPendingVerificationEmail,
} from "../../store/slices/authSlice";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import logo from "../../assets/logo.png";
import bot from "../../assets/bot.GIF";

interface LoginForm {
  email_or_username: string;
  password: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error, isAuthenticated, pendingVerificationEmail } =
    useSelector((state: RootState) => state.auth);
  const [showPassword, setShowPassword] = useState(false);
  const siteName = process.env.REACT_APP_SITE_NAME || "XRP Trading Platform";

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard");
    }
  }, [isAuthenticated, navigate]);

  // Handle redirect when pendingVerificationEmail is set
  useEffect(() => {
    if (pendingVerificationEmail) {
      console.log(
        "Redirecting to email verification for:",
        pendingVerificationEmail,
      );
      navigate("/verify-email", {
        state: { email: pendingVerificationEmail },
      });
      // Optionally clear the pending email after redirect
      // dispatch(clearPendingVerificationEmail());
    }
  }, [pendingVerificationEmail, navigate, dispatch]);

  const onSubmit = async (data: LoginForm) => {
    console.log("Login form submitted:", data);

    try {
      const result = await dispatch(login(data));

      // Check if login was rejected due to email verification
      if (login.rejected.match(result)) {
        const payload = result.payload as any;

        // Check if it's an email verification error from the API
        if (
          payload?.error_type === "email_verification_required" ||
          payload?.type === "email_verification_required"
        ) {
          // Extract email from the error payload
          const verificationEmail = payload.email || data.email_or_username;

          // Manually set pending verification email to trigger the useEffect redirect
          dispatch(setPendingVerificationEmail(verificationEmail));
          console.log(
            "Email verification required, setting pending email:",
            verificationEmail,
          );

          return;
        }
      }

      // If login was successful (not rejected), the useEffect will handle dashboard redirect
    } catch (error) {
      console.error("Login submission error:", error);
    }
  };

  return (
    <div className={globalStyle.container}>
      <main className="page-content2">
        <div className="page-content2-bg"></div>
        <div className="page-content2-wrapper">
          <div className="page-content2-content">
            <div className="max-w-md mx-auto py-8 md:py-12 gap-10">
              <Link to="/" className="text-center mb-8">
                <img
                  src={logo}
                  alt={siteName}
                  className="w-16 h-16 mx-auto mb-4"
                />
                <h2 className={globalStyle.h3}>Welcome Back</h2>
                <p className={globalStyle.body2 + " text-gray-400"}>
                  Sign in with your email or username
                </p>
              </Link>

              <Card padding="lg" className="mt-6">
                {error && (
                  <Alert
                    type="error"
                    message={
                      typeof error === "string"
                        ? error
                        : (error as any)?.message || "An error occurred"
                    }
                    onClose={() => dispatch(clearError())}
                    className="mb-6"
                  />
                )}

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>
                      Email or Username
                    </label>
                    <input
                      type="text"
                      className={globalStyle.input}
                      placeholder="Enter your email or username"
                      {...register("email_or_username", {
                        required: "Email or username is required",
                      })}
                    />
                    {errors.email_or_username && (
                      <p className={globalStyle.formError}>
                        {errors.email_or_username.message}
                      </p>
                    )}
                  </div>

                  <div className={globalStyle.formGroup}>
                    <label className={globalStyle.label}>Password</label>
                    <div className="relative">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={globalStyle.input}
                        placeholder="Enter your password"
                        {...register("password", {
                          required: "Password is required",
                          minLength: {
                            value: 8,
                            message: "Password must be at least 8 characters",
                          },
                        })}
                      />
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-300"
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? "Hide" : "Show"}
                      </button>
                    </div>
                    {errors.password && (
                      <p className={globalStyle.formError}>
                        {errors.password.message}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="remember"
                        className="w-4 h-4 text-primary border-gray-600 rounded focus:ring-primary bg-gray-800"
                      />
                      <label
                        htmlFor="remember"
                        className="ml-2 text-sm text-gray-400"
                      >
                        Remember me
                      </label>
                    </div>
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary hover:text-primary-300"
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <Button type="submit" fullWidth loading={isLoading} size="lg">
                    Sign In
                  </Button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-800">
                  <p className="text-center text-sm text-gray-400">
                    Don't have an account?{" "}
                    <Link
                      to="/register"
                      className="text-primary font-medium hover:text-primary-300"
                    >
                      Sign up
                    </Link>
                  </p>
                </div>
              </Card>

              <div className="mt-8 text-center">
                <p className={globalStyle.caption + " text-gray-500"}>
                  By signing in, you agree to our Terms of Service and Privacy
                  Policy
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* <div className="fixed top-10 left-10 z-30 pointer-events-none">
          <div className="relative">
            <div className="animate-float">
              <img
                src={bot}
                alt="AI Trading Bot Active"
                className="w-24 h-24 md:w-28 md:h-28 opacity-90 drop-shadow-lg"
              />
            </div>
          </div>
        </div> */}
      </main>
    </div>
  );
};

export default Login;
